import { EmbeddedMessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { AudienceList, CampaignType } from 'modules/domains/campaign';
import { Experiment } from 'modules/domains/experiment';
import { Locale } from 'modules/domains/template';
import {
  AmbiguousExpiration,
  AnchorTagsWithJS,
  CampaignStateDefinition,
  EmailContentDefinition,
  EmbeddedAction,
  EmbeddedContentDefinition,
  EmojiInButtonText,
  EmptyContent,
  EmptyWhatsAppButtonPhoneNumber,
  EmptyWhatsAppButtonText,
  EmptyWhatsAppButtonUrl,
  EmptyWhatsAppMessage,
  GRAPHQL_TYPENAMES,
  HasCycle,
  HasScriptTag,
  IframeTagWithJS,
  IllegalCharacters,
  IllegalScript,
  InAppContentDefinition,
  InvalidAttribute,
  InvalidEmail,
  InvalidExpiration,
  InvalidHandlebarComplexity,
  InvalidHandlebarCount,
  InvalidImageExtension,
  InvalidImageSize,
  InvalidMessageTypeId,
  InvalidSnippetParams,
  InvalidSnippetUsage,
  InvalidSubject,
  InvalidTemplateContents,
  InvalidTemplateName,
  InvalidUrlFormat,
  InvalidWhatsAppButtonUrlFormat,
  InvalidWhatsAppButtonUrlParams,
  InvalidWhatsAppFooter,
  JSInAnchorTag,
  JSInIframeTag,
  JsonTestData,
  MediumContent,
  MessageMedium,
  MessageType,
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  MissingLink,
  MissingSnippets,
  MissingUnsubscribeUrl,
  NotRenderable,
  PreheaderTooLong,
  PushContentDefinition,
  SMSContentDefinition,
  TemplateNotFound,
  TemplateResponse,
  Unrenderable,
  UnrenderableWithIndex,
  User,
  WebPushContentDefinition,
  WhatsAppContentDefinition,
  WhatsAppCategoryUpdateAttemptedAfterSubmission,
  InvalidTemplateLocale,
  WhatsAppEmojiLimitExceeded,
} from 'modules/graphql/types';
import { WhatsAppSender } from 'modules/sendingPlatforms/types/whatsAppSenders';
import { FC, ReactNode, RefObject } from 'react';
import { ErrorTheme } from 'ui/inputs/types';
import { BaseOption } from 'ui/selectv2/types';
import { Nullable } from 'utils/tsHelpers';

export type LinkParameterInputProps = {
  /** The function to run when the key input is updated */
  onKeyChange: (newKey: string) => void;
  /** The function to run when the value input is updated */
  onValueChange: (newValue: string) => void;
  /** The function to run when the input field is deleted */
  onDelete: () => void;
  /** Key input entry */
  keyInput: string;
  /** Value input entry */
  valueInput: string;
};

export enum TemplateEditViews {
  /** View the settings of an already setup and configured template */
  settings = 'settings',
  content = 'content',
  /**
   * View how the template would look with live data
   * Provides a JSON editor to customize the data and see how the template would change
   */
  preview = 'preview',
  /** Select a template from a list of templates */
  select = 'select',
}

export enum EditorView {
  AMP = 'amp',
  HTML = 'html',
  TEXT = 'text',
}

export enum InitTemplateTrigger {
  LocaleUpdate = 'localeUpdate',
  TemplateFirstLoad = 'templateFirstLoad',
  TemplateUpdate = 'templateUpdate',
}

export type TemplateCommon = {
  templateId: number;
  cacheDataFeed: boolean;
  createdAt?: string;
  creatorUser: User;
  dataFeedIds: number[];
  mergeDataFeedContext: boolean;
  messageType: Partial<MessageType>;
  name: string;
  referenceDataFeedsByAlias: boolean;
  shortenLinks?: boolean;
  templateType?: TemplateType;
  updatedAt?: string;
  updatedByUser: User;
  jsonTestData?: JsonTestData;
};

type ContentFormDataAll = {
  mediumData: MediumContent[];
};

type ContentFormDataLocale = {
  mediumData: MediumContent;
};

export type TemplateAndContentFormData = TemplateCommon & ContentFormDataAll;

export type TemplateAndContentFormDataLocale = TemplateCommon & ContentFormDataLocale;

export type TemplateType = 'BusinessInitiated' | 'UserInitiated' | null;

export enum UnselectedMessageMedium {
  Unselected = 'Unselected',
}

export type SelectedMessageMedium =
  | MessageMedium
  | UnselectedMessageMedium
  | EmbeddedMessageMedium
  | WhatsAppMedium;

export type MediumDataKey =
  | keyof EmailContentDefinition
  | keyof InAppContentDefinition
  | keyof PushContentDefinition
  | keyof SMSContentDefinition
  | keyof WebPushContentDefinition
  | keyof EmbeddedContentDefinition
  | keyof WhatsAppContentDefinition
  | 'inboxMetadata.subtitle'
  | 'inboxMetadata.title'
  | 'ottPayload.dialogHeader'
  | 'ottPayload.dialogText'
  | 'header.mediaUrl'
  | 'cannotInsert'
  | 'shortenLinks'
  | 'templateName';

export type InterruptionLevel = {
  id: number;
  value: string;
  label: string;
  subtext: string;
  disabledSubtext?: string;
  isDisabled?: boolean;
};

export type TemplateCampaign = {
  campaignState: CampaignStateDefinition;
  campaignTypeDefinition: CampaignType;
  id: number;
  name: string;
  experiments: Experiment[];
  lists: Pick<AudienceList, 'id'>[];
  mobileAppIds: number[];
};

export type GraphQLTypeNameData<GraphQLTypeName extends GRAPHQL_TYPENAMES> = {
  __typename: GraphQLTypeName;
};

export type GraphQLTemplateCampaign = TemplateCampaign &
  GraphQLTypeNameData<GRAPHQL_TYPENAMES.CAMPAIGN>;

export enum MenuOptionNames {
  EDIT_A_B_EXPERIMENT = 'Edit experiment',
  CREATE_A_B_EXPERIMENT = 'Create A/B experiment',
  PREVIEW_IN_DEVICE = 'Preview in device',
  PREVIEW_IN_NEW_TAB = 'Preview in new tab',
  SAVE_AS_NEW_TEMPLATE = 'Save as new template',
  EXPORT_HTML = 'Export html',
  REVISION_HISTORY = 'Revision history',
  SPAM_REPORT = 'Spam report',
}

export enum OS {
  android = 'android',
  ios = 'ios',
}

// purposely ordered this way per design
export enum PreviewType {
  notification = 'Notification',
  message = 'Message',
  contactCard = 'Contact card details',
}

export enum PlatformWebPush {
  Desktop = 'Desktop',
  Mobile = 'Mobile',
}

export enum OSWebPush {
  iOS = 'iOS',
  Android = 'Android',
  MacOS = 'MacOS',
  Windows = 'Windows',
}

export enum EmailPreviewType {
  Desktop = 'Desktop',
  Mobile = 'Mobile',
}

export enum PushPreviewType {
  expandedNotification = 'Expanded notification',
  notification = 'Notification',
}

/** This is used to compare the types of opt-out message in Template Editor page. */
export enum OptOutDisclaimerOptions {
  onEverySMS = 'OnEverySMS',
  onFirstSMS = 'OnFirstSMS',
  disable = 'Disable' /** Currently not getting any data (null) if SMS is disabled in project settings. */,
}

// Build GqlUpsertTemplateFailure based on UpsertTemplateFailure in order to access __typename
// (this appends the appropriate __typename to the types defined in 'modules/graphql/types')
export enum GqlUpsertTemplateFailureTypes {
  INVALID_TEMPLATE_NAME = 'InvalidTemplateName',
  INVALID_TEMPLATE_LOCALE = 'InvalidTemplateLocale',
  INVALID_TEMPLATE_CONTENTS = 'InvalidTemplateContents',
  TEMPLATE_NOT_FOUND = 'TemplateNotFound',
}

export type GenericUpsertTemplate = ({ variables }: Record<string, unknown>) => void;

export type GqlUpsertTemplateFailureTypeNameData<
  GraphQLTypeName extends GqlUpsertTemplateFailureTypes,
> = {
  __typename: GraphQLTypeName;
};

export type GqlInvalidTemplateContents = InvalidTemplateContents &
  GqlUpsertTemplateFailureTypeNameData<GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_CONTENTS>;
export type GqlInvalidTemplateName = InvalidTemplateName &
  GqlUpsertTemplateFailureTypeNameData<GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_NAME>;
export type GqlInvalidTemplateLocale = InvalidTemplateLocale &
  GqlUpsertTemplateFailureTypeNameData<GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_LOCALE>;
export type GqlTemplateNotFound = TemplateNotFound &
  GqlUpsertTemplateFailureTypeNameData<GqlUpsertTemplateFailureTypes.TEMPLATE_NOT_FOUND>;

export type GqlUpsertTemplateFailure =
  | GqlInvalidTemplateContents
  | GqlInvalidTemplateName
  | GqlInvalidTemplateLocale
  | GqlTemplateNotFound;

// Build GqlTemplateResponse based on TemplateResponse in order to access __typename
// (this appends the appropriate __typename to the types defined in 'modules/graphql/types')
export enum GqlTemplateResponseTypes {
  NO_TEMPLATE_FOUND = 'NoTemplateFound',
  TEMPLATE_RESPONSE = 'TemplateResponse',
  CORRUPTED_TEMPLATE_DATA = 'CorruptedTemplateData',
}

export type GqlTemplateResponseTypeNameData<GraphQLTypeName extends GqlTemplateResponseTypes> = {
  __typename: GraphQLTypeName;
};

export type GqlTemplateResponse = TemplateResponse &
  GqlTemplateResponseTypeNameData<GqlTemplateResponseTypes>;

export enum GqlUpsertTemplateFieldInvalidFailureTypes {
  ANCHOR_TAGS_WITH_JS = 'AnchorTagsWithJS',
  HAS_CYCLE = 'HasCycle',
  HAS_SCRIPT_TAG = 'HasScriptTag',
  IFRAME_TAG_WITH_JS = 'IframeTagWithJS',
  ILLEGAL_CHARACTERS = 'IllegalChracters',
  NOT_RENDERABLE = 'NotRenderable',
}

export type GqlUpsertFieldInvalidFailureTypeNameData<
  GraphQLTypeName extends GqlUpsertTemplateFieldInvalidFailureTypes,
> = {
  __typename: GraphQLTypeName;
};

export type GqlAnchorTagsWithJS = AnchorTagsWithJS &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.ANCHOR_TAGS_WITH_JS>;
export type GqlHasCycle = HasCycle &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.HAS_CYCLE>;
export type GqlHasScriptTag = HasScriptTag &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.HAS_SCRIPT_TAG>;
export type GqlIframeTagWithJS = IframeTagWithJS &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.IFRAME_TAG_WITH_JS>;
export type GqlIllegalChracters = IllegalCharacters &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.ILLEGAL_CHARACTERS>;
export type GqlNotRenderable = NotRenderable &
  GqlUpsertFieldInvalidFailureTypeNameData<GqlUpsertTemplateFieldInvalidFailureTypes.NOT_RENDERABLE>;

export enum GqlUpsertTemplateInvalidContentFailureTypes {
  AMBIGUOUS_EXPIRATION = 'AmbiguousExpiration',
  EMOJI_IN_BUTTON_TEXT = 'EmojiInButtonText',
  WHATSAPP_EMOJI_LIMIT_EXCEEDED = 'WhatsAppEmojiLimitExceeded',
  EMPTY_CONTENT = 'EmptyContent',
  EMPTY_WHATSAPP_BUTTON_TEXT = 'EmptyWhatsAppButtonText',
  EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER = 'EmptyWhatsAppButtonPhoneNumber',
  EMPTY_WHATSAPP_BUTTON_URL = 'EmptyWhatsAppButtonUrl',
  EMPTY_WHATSAPP_MESSAGE = 'EmptyWhatsAppMessage',
  ILLEGAL_SCRIPT = 'IllegalScript',
  INVALID_ACTION_BUTTON_URI = 'InvalidActionButtonUri',
  INVALID_ATTRIBUTE = 'InvalidAttribute',
  INVALID_DEFAULT_ACTION = 'InvalidDefaultAction',
  INVALID_EMAIL = 'InvalidEmail',
  INVALID_EXPIRATION = 'InvalidExpiration',
  INVALID_MEDIA_URL = 'InvalidMediaUrl',
  INVALID_MESSAGE_TYPE_ID = 'InvalidMessageTypeId',
  INVALID_SNIPPET_PARAMS = 'InvalidSnippetParams',
  INVALID_SUBJECT = 'InvalidSubject',
  JS_IN_ANCHOR_TAG = 'JSInAnchorTag',
  JS_IN_IFRAME_TAG = 'JSInIframeTag',
  INVALID_HANDLEBAR_COUNT = 'InvalidHandlebarCount',
  INVALID_HANDLEBAR_COMPLEXITY = 'InvalidHandlebarComplexity',
  INVALID_IMAGE_EXTENSION = 'InvalidImageExtension',
  INVALID_IMAGE_SIZE = 'InvalidImageSize',
  INVALID_SNIPPET_USAGE = 'InvalidSnippetUsage',
  INVALID_WHATSAPP_BUTTON_URL_PARAMS = 'InvalidWhatsAppButtonUrlParams',
  INVALID_WHATSAPP_BUTTON_URL_FORMAT = 'InvalidWhatsAppButtonUrlFormat',
  INVALID_WHATSAPP_FOOTER = 'InvalidWhatsAppFooter',
  MISSING_SNIPPETS = 'MissingSnippets',
  MISSING_UNSUBSCRIBE_URL = 'MissingUnsubscribeUrl',
  PREHEADER_TOO_LONG = 'PreheaderTooLong',
  UNRENDERABLE = 'Unrenderable',
  UNRENDERABLE_WITH_INDEX = 'UnrenderableWithIndex',
  MISSING_LINK = 'MissingLink',
  MISSING_MESSAGE_OR_SILENT_PUSH = 'MissingMessageOrSilentPush',
  INVALID_ROKU_FONT_URI = 'InvalidRokuFontUri',
  INVALID_URL_FORMAT = 'InvalidUrlFormat',
  INVALID_IMAGE_URI = 'InvalidImageURI',
  INVALID_ROKU_HEX_VALUE = 'InvalidRokuHexValue',
  INVALID_REQUIRED_ROKU_CONTENT = 'InvalidRequiredRokuContent',
  WHATS_APP_CATEGORY_UPDATE_ATTEMPTED_AFTER_SUBMISSION = 'WhatsAppCategoryUpdateAttemptedAfterSubmission',
}

export type GqlUpsertInvalidContentFailureTypeNameData<
  GraphQLTypeName extends GqlUpsertTemplateInvalidContentFailureTypes,
> = {
  __typename: GraphQLTypeName;
};

export type GqlUnrenderable = Unrenderable &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE>;
export type GqlUnrenderableWithIndex = UnrenderableWithIndex &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX>;
export type GqlMissingSnippets = MissingSnippets &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.MISSING_SNIPPETS>;
export type GqlInvalidSnippetParams = InvalidSnippetParams &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_PARAMS>;
export type GqlAmbiguousExpiration = AmbiguousExpiration &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.AMBIGUOUS_EXPIRATION>;
export type GqlEmojiInButtonText = EmojiInButtonText &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT>;
export type GqlWhatsAppEmojiLimitExceeded = WhatsAppEmojiLimitExceeded &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.WHATSAPP_EMOJI_LIMIT_EXCEEDED>;
export type GqlEmptyContent = EmptyContent &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_CONTENT>;
export type GqlEmptyWhatsAppButtonText = EmptyWhatsAppButtonText &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT>;
export type GqlEmptyWhatsAppButtonPhoneNumber = EmptyWhatsAppButtonPhoneNumber &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER>;
export type GqlEmptyWhatsAppButtonUrl = EmptyWhatsAppButtonUrl &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL>;
export type GqlWhatsAppCategoryUpdateAttemptedAfterSubmission =
  WhatsAppCategoryUpdateAttemptedAfterSubmission &
    GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.WHATS_APP_CATEGORY_UPDATE_ATTEMPTED_AFTER_SUBMISSION>;
export type GqlIllegalScript = IllegalScript &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.ILLEGAL_SCRIPT>;
export type GqlInvalidEmail = InvalidEmail &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EMAIL>;
export type GqlInvalidExpiration = InvalidExpiration &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_EXPIRATION>;
export type GqlInvalidMessageTypeId = InvalidMessageTypeId &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_MESSAGE_TYPE_ID>;
export type GqlInvalidHandlebarCount = InvalidHandlebarCount &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT>;
export type GqlInvalidHandlebarComplexity = InvalidHandlebarComplexity &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY>;
export type GqlEmptyWhatsAppMessage = EmptyWhatsAppMessage &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_MESSAGE>;
export type GqlInvalidWhatsAppButtonUrlParams = InvalidWhatsAppButtonUrlParams &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS>;
export type GqlInvalidWhatsAppButtonUrlFormat = InvalidWhatsAppButtonUrlFormat &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT>;
export type GqlInvalidWhatsAppFooter = InvalidWhatsAppFooter &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_FOOTER>;
export type GqlInvalidSnippetUsage = InvalidSnippetUsage &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE>;
export type GqlInvalidUrlFormat = InvalidUrlFormat &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_URL_FORMAT>;
export type GqlInvalidImageSize = InvalidImageSize &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_SIZE>;
export type GqlInvalidImageExtension = InvalidImageExtension &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_IMAGE_EXTENSION>;
export type GqlInvalidSubject = InvalidSubject &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SUBJECT>;
export type GqlJSInAnchorTag = JSInAnchorTag &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_ANCHOR_TAG>;
export type GqlJSInIframeTag = JSInIframeTag &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.JS_IN_IFRAME_TAG>;
export type GqlMissingUnsubscribeUrl = MissingUnsubscribeUrl &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.MISSING_UNSUBSCRIBE_URL>;
export type GqlPreheaderTooLong = PreheaderTooLong &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.PREHEADER_TOO_LONG>;
export type GqlMissingLink = MissingLink &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.MISSING_LINK>;
export type GqlInvalidAttribute = InvalidAttribute &
  GqlUpsertInvalidContentFailureTypeNameData<GqlUpsertTemplateInvalidContentFailureTypes.INVALID_ATTRIBUTE>;

export type GqlInvalidSecurityErrors =
  | GqlIllegalScript
  | GqlJSInAnchorTag
  | GqlJSInIframeTag
  | GqlInvalidAttribute;

export type GqlInvalidContentErrors =
  | GqlAmbiguousExpiration
  | GqlMissingSnippets
  | GqlInvalidSnippetParams
  | GqlInvalidEmail
  | GqlInvalidMessageTypeId
  | GqlUnrenderable
  | GqlUnrenderableWithIndex
  | GqlMissingUnsubscribeUrl
  | GqlInvalidHandlebarCount
  | GqlInvalidHandlebarComplexity
  | GqlInvalidSnippetUsage
  | GqlInvalidWhatsAppButtonUrlParams
  | GqlInvalidWhatsAppButtonUrlFormat
  | GqlInvalidImageExtension
  | GqlInvalidImageSize
  | GqlInvalidUrlFormat
  | GqlInvalidWhatsAppFooter
  | GqlEmptyWhatsAppMessage
  | GqlEmojiInButtonText
  | GqlEmptyWhatsAppButtonPhoneNumber
  | GqlEmptyWhatsAppButtonText
  | GqlEmptyWhatsAppButtonUrl
  | GqlMissingLink
  | GqlWhatsAppCategoryUpdateAttemptedAfterSubmission;

export type GqlInvalidContentWarnings =
  | GqlEmptyContent
  | GqlInvalidSubject
  | GqlInvalidSubject
  | GqlPreheaderTooLong;

export type GqlInvalidContentReason =
  | GqlInvalidContentErrors
  | GqlInvalidContentWarnings
  | GqlInvalidSecurityErrors;

export type FailureErrorReason = {
  __typename: GqlUpsertTemplateInvalidContentFailureTypes;
  errorMsg?: string;
  fieldName?: FieldNames | string;
  invalidButtonActions?: EmbeddedAction[];
  msg?: string;
  snippets?: string[];
  snippetsWithParams?: string[];
  index?: number;
};

export type ValidationFailureErrors = {
  contentId?: number;
  reasons: FailureErrorReason[];
};

export type ValidationFailures = {
  errors: ValidationFailureErrors[];
};

export type TextInputFieldProps = {
  isDisabled?: boolean;
  maxLength?: number;
  mediumRef?: RefObject<HTMLInputElement> | null;
  mediumDataKey: MediumDataKey;
  mediumDataValue?: string | string[];
  showSnippet?: boolean;
  placeholderText?: string;
  changeHandler: (url: string) => void;
  placeholder?: string;
  hasError?: boolean;
  hasWarning?: boolean;
  Suffix?: ReactNode | null;
};

export enum MessagingPermissionOptions {
  VIEW_ONLY = 'ViewOnly',
  VIEW_AND_DRAFT = 'ViewAndDraft',
  ALL = 'All',
  NONE = 'NONE',
}

export type TextAreaFieldProps = {
  /** disabled styles applied to text area */
  isDisabled?: boolean;
  /** prop to override isUnset value */
  isUnset?: boolean;
  /** medium data key */
  mediumDataKey: MediumDataKey;
  /** the content inside the text area */
  mediumDataValue?: string;
  /** if secondary text for text area is visible */
  hasSecondaryText?: boolean;
  /** change handler for entering text in text area */
  onChangeHandler: (content: string) => void;
  /** minimum height of text area */
  minHeight?: number;
  /** maximum characters allowed in text area */
  maxCharacters?: number;
  /** Placeholder text for the text area */
  placeholderText?: string;
  mediumRef?: RefObject<HTMLTextAreaElement> | null;
  /** if there's an error */
  hasError?: boolean;
  /** if there's a warning */
  hasWarning?: boolean;
  /** toggles merge tag button */
  showMergeTag?: boolean;
  /** toggles markdown formatting buttons */
  showMarkdownOptions?: boolean;
};

export type ClientSideValidationType = {
  isTemplateFieldsValid: boolean;
  errorFields: Record<string, string>;
};

type ValidatorType<T> = (val: T) => boolean;
export type RuleType<T> = {
  validator: ValidatorType<T>;
  errorMessage: string;
};
export type ValidationSchemaType<T> = {
  [k in keyof T]?: RuleType<T[k]>[];
};

export type UpdateMediumFieldsType = {
  [k in MediumDataKey]?: any;
};

export enum SendTestMessageErrorTypes {
  NO_RECIPIENTS_FOUND = 'NoRecipientsFound',
  SMS_MISSING_FIELDS = 'SMSMissingFields',
  WEB_PUSH_MISSING_FIELDS = 'WebPushMissingFields',
  PUSH_MISSING_FIELDS = 'PushMissingFields',
}

export enum SendTemplateTypes {
  TEST_MESSAGE = 'test message',
  PROOF = 'proof',
}

export enum ACTION_BUTTON_DROP_DOWN_SELECTIONS {
  NORMAL = 'Normal',
  DESTRUCTIVE = 'Destructive',
  TEXT_INPUT = 'Text input',
}

export enum ACTION_BUTTON_RADIO_OPTIONS {
  OPEN_APP = 'openApp',
  TRACK_ONLY = 'trackOnly',
  OPEN_URL = 'openUrl',
  CUSTOM_ACTION = 'customAction',
}

export enum ActionButtonPreview {
  single = 'single',
  first = 'first',
  middle = 'middle',
  last = 'last',
  expandedSingle = 'expandedSingle',
  expandedFirst = 'expandedFirst',
  expandedMiddle = 'expandedMiddle',
  expandedLast = 'expandedLast',
}

export type DragDropOption = {
  altText: string;
  id: string;
  label: string;
  svg: FC<React.PropsWithChildren<unknown>>;
};

export type DragDropTemplateMetadata = {
  description: string;
  json?: object;
  slug: string;
  templateTitle: string;
};

export type DragDropTemplateOption = {
  altText: string;
  id: string;
  label: string;
  metadata: string;
  svg: FC<React.PropsWithChildren<unknown>>;
};

export type UpsertConfig = {
  onComplete: () => void;
  onError: (errors: Record<string, string>) => void;
  onSuccess: (templateId: number) => void;
};

export enum ErrorType {
  SECURITY_ERRORS = 'securityErrors',
  CONTENT_ERRORS = 'contentErrors',
  CONTENT_WARNINGS = 'contentWarnings',
}

export type ErrorValidations = {
  securityErrors: GqlInvalidSecurityErrors[];
  contentErrors: GqlInvalidContentErrors[];
  contentWarnings: GqlInvalidContentWarnings[];
};

export type ValidationError = {
  __typename: GqlUpsertTemplateInvalidContentFailureTypes;
  contentId: number;
  errorMessage?: string;
  fieldName: FieldNames;
  invalidButtonActions?: EmbeddedAction[];
  isRoku?: boolean;
  locale: string;
  subMessage?: string;
  index?: number;
};

export type ErrorValidationConstruct = {
  errors: ValidationFailureErrors[];
};

export interface ErrorValidationConstructReturn {
  contentErrors: ValidationError[];
  contentWarnings: ValidationError[];
}

export interface ErrorValidationFieldNameConstruct extends ErrorValidationConstructReturn {
  fieldName: FieldNames;
}

/**
 * TODO: we currently have multiple field names with the same values,
 * which is causing TS errors because enum properties cannot have the same value
 */
export enum FieldNames {
  ACTION_BUTTONS = 'Action buttons',
  AMP_HTML = 'AMP HTML',
  ATTACHMENT_URL = 'Media URL',
  ATTACHMENT_URL_ANDROID = 'Media URL - Android',
  BADGE = 'Badge Count',
  BCC_EMAILS = 'BCC',
  BODY = 'Body',
  BUTTONS = 'Buttons',
  CATEGORY = 'Category',
  CC_EMAILS = 'CC',
  CLICK_ACTION = 'Click location',
  DATA_FEED_ID = 'Data feeds',
  DEEPLINK = 'Open Action',
  DEFAULT_ACTION = 'Open Action',
  DISPLAY_SETTINGS = 'Position',
  EXPIRATION_DATE_TIME_ABSOLUTE = 'Expiration time',
  FOOTER = 'Footer',
  FROM_EMAIL = 'From email',
  FROM_NAME = 'From name',
  FROM_PHONE_NUMBER_ID = 'Sender',
  GOOGLE_ANALYTICS_CAMPAIGN_NAME = 'Google Analytics parameter',
  HTML = 'HTML',
  HTML_DATA_FEEDS = 'HTML Data feed',
  ICON = 'Icon',
  IMAGE_URL = 'Image URL',
  INBOX_METADATA_ICON = 'Mobile inbox thumbnail',
  INBOX_METADATA_SUBTITLE = 'Mobile inbox body',
  INBOX_METADATA_TITLE = 'Mobile inbox title',
  INTERRUPTION_LEVEL = 'Interruption level',
  LINK_PARAMS = 'Link parameters',
  LINK_SHORTENING = 'Shorten links',
  MESSAGE = 'Message',
  MESSAGE_TYPE = 'Message type',
  MISSING_MESSAGE_OR_SILENT_PUSH = 'Message or deliver silently',
  PAYLOAD = 'Raw data (JSON)',
  PLAIN_TEXT = 'Plain text',
  PREHEADER_TEXT = 'Preheader',
  RELEVANCE_SCORE = 'Releveance score',
  REPLY_TO = 'Reply to',
  ROKU_ACTION_BUTTONS = 'Roku action buttons',
  ROKU_BACKGROUND_COLOR = 'Roku background color',
  ROKU_BODY = 'Roku body',
  ROKU_BODY_FONT = 'Roku body font',
  ROKU_BUTTON_FONT = 'Roku button font',
  ROKU_FHD_IMAGE = 'Roku image url',
  ROKU_TITLE = 'Roku title',
  ROKU_TITLE_FONT = 'Roku title font',
  SOUND = 'Custom sound',
  SUBJECT = 'Subject',
  TITLE = 'Title',
  TRIGGER = 'Preset animations',
  WEB_DISPLAY_SETTINGS = 'Position',
  HEADER = 'Header',
}

export type ErrorThemeDisplay = ErrorTheme & { errorMessage: string };

export enum ContentType {
  TITLE = 'title',
  BODY = 'body',
}

export type ContentIdKey = {
  pushContentId?: number;
  webPushContentId?: number;
  smsContentId?: number;
  emailContentId?: number;
  inAppContentId?: number;
};

export type LocaleErrorContent = ContentIdKey & {
  locale: { name: string };
};

export enum PreviewWithDataErrorTypes {
  HANDLEBARS_EXCEPTION = 'HandlebarsException',
  SEND_ABORTED_EXCEPTION = 'SendAbortException',
}

export type PreviewWithDataErrorMessage = {
  label: string;
  errorMessage: string;
};

export enum GqlUpdateDefaultLocaleFailureTypes {
  NOT_FOUND = 'LocaleNotFound',
  LOCALES_DISABLED = 'LocalesDisabledForProject',
}

export type DragAndDropCallback = ({ html }: { html: string }) => void;

export enum CustomFontViewMode {
  Create = 'create',
  Edit = 'edit',
  Empty = 'empty',
  View = 'view',
}

export type BeeBuilderMetadata = Record<string, any>;

export enum CONTENT_SELECTION_OPTIONS {
  CONTENT = 'CONTENT',
  RAW = 'RAW',
  EVERYTHING = 'EVERYTHING',
}

export type InAppDisplaySetting = {
  percentage?: number;
  displayOption?: string;
};

export type InAppDisplaySettings = {
  bottom: InAppDisplaySetting;
  top: InAppDisplaySetting;
  left: InAppDisplaySetting;
  right: InAppDisplaySetting;
  shouldAnimate?: boolean;
  bgColor?: {
    hex: string;
    alpha: number;
  };
};

export type LocaleToContentId = {
  [key: string]: number;
};

export type TEMPLATE = {
  content: TemplateContent;
  metadata: TemplateMetadata;
};

export enum IN_APP_TRIGGER_OPTION {
  NEVER = 'never',
  IMMEDIATE = 'immediate',
}

export type InAppTrigger = {
  type: IN_APP_TRIGGER_OPTION;
};

export type TemplateContent = {
  beeBuilderMetadataOpt?: BeeBuilderMetadata;
  contentSelection: CONTENT_SELECTION_OPTIONS;
  createdAt: number;
  creatorUserId: string;
  expirationDateTime?: number;
  expirationDuration?: string;
  html: string;
  id: number;
  inAppDisplaySettings: InAppDisplaySettings;
  inboxMetadata?: {
    title: string;
    subtitle: string;
    icon?: string;
  };
  saveToInbox?: boolean;
  payload?: {
    [key: string]: any;
  };
  templateId: number;
  trigger: InAppTrigger;
  webInAppDisplaySettings?: WebInAppDisplayType;
};

export type WhatsAppLocaleContent = {
  locale: Nullable<string>;
  languageName: Nullable<string>;
  metaApprovalStatus: Nullable<MetaApprovalStatusDefinition>;
  qualityRating: Nullable<MetaQualityRating>;
  whatsAppSender: Nullable<Pick<WhatsAppSender, 'sendLimit' | 'status'>>;
};

export type TemplateMetadata = {
  cacheDataFeed: boolean;
  campaignId?: number;
  createdAt: number;
  creatorUserId: string;
  dataFeedIds: any[];
  experimentId?: number;
  id: number;
  inAppContentId?: number;
  localeToContentId?: LocaleToContentId;
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  locale: string;
  localeContents?: WhatsAppContentDefinition[];
  projectId: number;
  referenceDataFeedsByAlias: boolean;
  teamId?: number;
  updatedAt: number;
  updatedBy?: string;
  whatsAppLocaleContent?: WhatsAppLocaleContent[];
};

export enum WEB_IN_APP_POSITION {
  BOTTOM_RIGHT = 'BottomRight',
  CENTER = 'Center',
  FULL = 'Full',
  TOP_RIGHT = 'TopRight',
}

export type WebInAppDisplayType = {
  bgColor?: {
    hex: string;
    alpha: number;
  };
  position: WEB_IN_APP_POSITION;
  shouldAnimate?: boolean;
};

export interface LocaleOption extends BaseOption {
  data: Locale;
  id: string;
}

export enum UserProfileDataType {
  Email = 'email',
  UserId = 'userId',
}

export type LinksCompatibleMediumData =
  | SMSContentDefinition
  | EmailContentDefinition
  | WhatsAppContentDefinition;
